import {
  ArrowForwardRounded,
  CheckCircleRounded,
  FlagRounded,
} from "@mui/icons-material";
import {
  Backdrop,
  Box,
  Button,
  Fade,
  Grid,
  Modal,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { GetAllAlmacenesList } from "../../../../hooks/getListAlmacen";
import { useLoading } from "../../../../components/Loading/useLoading";
import { useSnackbar } from "notistack";
import { SelectAlmacenes } from "./selectAlmacenes";
import { ConfigDistribution } from "./configDistribution";
import { GetAllClientesList } from "../../../../hooks/getListClientes";
import { GetAllPartesList } from "../../../../hooks/getPartesList";
import { VisulizadorDistribucion } from "./visualizador";
import { CreatePlan } from "../../../../hooks/planProductivo";

const style = {
  position: "absolute",
  top: "45%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1200,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  display: "flex",
  justifyContent: "center",
  maxHeight: "80vh",
  overflowY: "scroll",
};
const BRAND = {
  primary: "#575E3B",
  primaryLight: "#eaebe9da",
  success: "#16A34A",
  surface: "#F8FAFC",
  border: "#E2E8F0",
  text: "#0F172A",
  muted: "#64748B",
};
const CustomConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 20,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    border: `2px solid ${BRAND.primary}`,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    backgroundColor: BRAND.primary,
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: BRAND.border,
    borderRadius: 2,
    transition: "background-color 0.4s ease",
  },
}));
const StepIconRoot = styled("div")(({ ownerState }) => ({
  width: 42,
  height: 42,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: 15,
  transition: "all 0.3s ease",
  ...(ownerState.completed && {
    background: BRAND.primary,
    color: "#fff",
    boxShadow: `0 0 0 4px ${BRAND.success}22`,
  }),
  ...(ownerState.active && {
    border: `2px solid ${BRAND.primary}`,
    color: `${BRAND.primary}`,
    boxShadow: `0 0 0 5px ${BRAND.primary}28`,
  }),
  ...(!ownerState.active &&
    !ownerState.completed && {
      background: "#fff",
      color: BRAND.muted,
      border: `2px solid ${BRAND.border}`,
    }),
}));

function StepPlaceholder({ label }) {
  return (
    <Box
      sx={{
        textAlign: "center",
        py: 6,
        px: 3,
        background: BRAND.primaryLight,
        borderRadius: 3,
        border: `1.5px dashed ${BRAND.primary}66`,
        width: "100%",
      }}
    >
      <Typography
        sx={{ color: BRAND.primary, fontWeight: 600, fontSize: 16, mb: 1 }}
      >
        {label}
      </Typography>
      <Typography sx={{ color: BRAND.muted, fontSize: 14 }}>
        Aquí irá el contenido de este paso.
      </Typography>
    </Box>
  );
}
function CustomStepIcon({ active, completed, icon }) {
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {completed ? (
        <CheckCircleRounded sx={{ fontSize: 22 }} />
      ) : (
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{icon}</Typography>
      )}
    </StepIconRoot>
  );
}

export const ModalNuevaPlaneacion = ({ open, handleClose }) => {
  const { createPlanMutation } = CreatePlan();
  const { getAllPartesListMutation } = GetAllPartesList();
  const { getAllClientesListMutation } = GetAllClientesList();
  const { getAllAlmacenesListMutation } = GetAllAlmacenesList();
  const { setAppLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();

  const [partes, setPartes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [almaSelected, setAlmaSelected] = useState([]);
  const [distributionConfig, setDistributionConfig] = useState([]);

  useEffect(() => {
    getAlmacenes();
    getClientes();
    getPartes();
  }, []);

  const handleCreatePlan = () => {
    setAppLoading(true);
    createPlanMutation(distributionConfig, {
      onSuccess: (data) => {
        enqueueSnackbar("Plan productivo creado exitosamente", {
          autoHideDuration: 2000,
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        });
      },
      onError: (error) => {
        enqueueSnackbar("Error al crear el plan productivo", {
          autoHideDuration: 2000,
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        });
      },
      onSettled: () => {
        setDistributionConfig([]);
        setAlmaSelected([]);
        setAppLoading(false);
      },
    });
  };

  //---------------STEEPER CONTENT-------------------------------------------------
  const STEPS = [
    {
      label: "Líneas",
      description: "Seleccionalas de acuerdo al orden deseado",
      component: (
        <SelectAlmacenes
          key={"step_1"}
          almacenes={almacenes}
          almaSelected={almaSelected}
          setAlmaSelected={setAlmaSelected}
          distributionConfig={distributionConfig}
          setDistributionConfig={setDistributionConfig}
        />
      ),
    },
    {
      label: "Distribución",
      description: "Ajuste la distribución de cada línea",
      component: (
        <ConfigDistribution
          key={"step_2"}
          almacenes={almacenes}
          almaSelected={almaSelected}
          distributionConfig={distributionConfig}
          setDistributionConfig={setDistributionConfig}
          clientes={clientes}
          partes={partes}
        />
      ),
    },
    {
      label: "Vista previa",
      description: "Asegurese de validar antes de finalizar",
      component: (
        <VisulizadorDistribucion
          key={"step_3"}
          almacenes={almacenes}
          almaSelected={almaSelected}
          distributionConfig={distributionConfig}
          clientes={clientes}
          partes={partes}
        />
      ),
    },
  ];

  const [activeStep, setActiveStep] = useState(0);

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === STEPS.length - 1;
  const isComplete = activeStep === STEPS.length;

  const handleNext = () => {
    if (!isLastStep) {
      setActiveStep((prev) => prev + 1);
    } else {
      console.log(
        "Finalizar proceso con esta configuración:",
        distributionConfig,
      );
      handleCreatePlan();
      setActiveStep(0);
      handleClose();
    }
  };
  const handleBack = () => setActiveStep((prev) => prev - 1);

  // ------------------------------------------------------------------------------

  const getAlmacenes = () => {
    setAppLoading(true);
    getAllAlmacenesListMutation(
      {},
      {
        onSuccess: (data) => {
          setAlmacenes(data);
        },
        onError: (error) => {
          enqueueSnackbar("Error al obtener los almacenes", {
            autoHideDuration: 2000,
            variant: "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center",
            },
          });
        },
        onSettled: () => {
          setAppLoading(false);
        },
      },
    );
  };
  const getClientes = () => {
    getAllClientesListMutation(
      {},
      {
        onSuccess: (data) => {
          data.sort((a, b) => {
            return a.label.localeCompare(b.label);
          });
          setClientes(data);
        },
        onError: (error) => {
          console.log(error);
        },
      },
    );
  };
  const getPartes = () => {
    setAppLoading(true);
    getAllPartesListMutation(
      {},
      {
        onSuccess: (data) => {
          setPartes(data);
        },
        onError: (error) => {
          console.log(error);
        },
        onSettled: () => {
          setAppLoading(false);
        },
      },
    );
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={open}>
        <Box sx={style}>
          <Grid container spacing={0}>
            <Grid item xs={12} className="center_item">
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                connector={<CustomConnector />}
                sx={{ mt: 3, width: "100%" }}
              >
                {STEPS.map((step) => (
                  <Step key={step.label}>
                    <StepLabel
                      StepIconComponent={CustomStepIcon}
                      sx={{
                        "& .MuiStepLabel-label": {
                          mt: 1,
                          fontSize: 13,
                          fontWeight: 600,
                          color: BRAND.muted,
                          "&.Mui-active": { color: BRAND.primary },
                          "&.Mui-completed": { color: BRAND.success },
                        },
                        "& .MuiStepLabel-labelContainer span:last-child": {
                          display: { xs: "none", sm: "block" },
                          fontSize: 11,
                          color: BRAND.muted,
                          fontWeight: 400,
                        },
                      }}
                    >
                      {step.label}
                      <span>{step.description}</span>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>
            <Grid item xs={12} className="center_item" sx={{ mt: 4, mb: 2 }}>
              {STEPS[activeStep].component}
            </Grid>
            <Grid item xs={12} className="center_item">
              {!isComplete && (
                <Box
                  sx={{
                    px: { xs: 3, md: 5 },
                    py: 3,
                    borderTop: `1px solid ${BRAND.border}`,
                    background: BRAND.surface,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={isFirstStep}
                    startIcon={
                      <ArrowForwardRounded
                        sx={{ transform: "rotate(180deg)" }}
                      />
                    }
                    sx={{
                      borderColor: BRAND.border,
                      color: BRAND.muted,
                      borderRadius: 2,
                      px: 3,
                      py: 1.2,
                      fontWeight: 600,
                      fontSize: 14,
                      textTransform: "none",
                      "&:hover": {
                        borderColor: BRAND.primary,
                        color: BRAND.primary,
                        background: BRAND.primaryLight,
                      },
                      "&.Mui-disabled": {
                        borderColor: BRAND.border,
                        color: BRAND.border,
                      },
                    }}
                  >
                    Regresar
                  </Button>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                      Paso {activeStep + 1} de {STEPS.length}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      endIcon={
                        isLastStep ? <FlagRounded /> : <ArrowForwardRounded />
                      }
                      sx={{
                        background: BRAND.primary,
                        borderRadius: 2,
                        px: 4,
                        py: 1.2,
                        fontWeight: 700,
                        fontSize: 14,
                        textTransform: "none",
                        boxShadow: `0 4px 14px ${BRAND.primary}44`,
                        "&:hover": {
                          background: "#40442d",
                          boxShadow: `0 6px 18px ${BRAND.primary}55`,
                        },
                      }}
                    >
                      {isLastStep ? "Finalizar" : "Siguiente paso"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Modal>
  );
};
